import { customSendNotifyBody } from '../../functions/shared/notify-helpers/customSendNotifyBody'
import sendMail from '../../functions/shared/email/sendMail'
import { getFirstName, interpolate } from './landlordEvents/helpers/stringHelpers'
import { grantRewardTrialForUser, hasRealPushToken, type RecipientPreview, type SendResult } from './eventHelpers'
import { EMAIL_TEMPLATE_IDS, standardEmails } from './landlordEvents/constants/events.constants'
import type { RunRecord, RunStep } from './safeTypes'
import { acquireRunLock, clearActiveRunIdLocal, clearRunLocal, releaseRunLock, setActiveRunIdLocal, snapshotRunLocal, touchRunLock } from './safeStorage'

const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms))

const DEFAULT_CHECKPOINT_EVERY_N = 300
const normalizeEmail = (value: string | null | undefined): string => (value ?? '').trim().toLowerCase()
const STANDARD_EMAILS_SET = new Set(standardEmails.map((email) => normalizeEmail(email)).filter(Boolean))

type ProgressUpdate = {
    step: RunStep
    done: number
    total: number
    statusMessage: string
    run: RunRecord
}

type RunnerOptions = {
    run: RunRecord
    recipients: RecipientPreview[]
    updateRun: (run: RunRecord) => Promise<void> | void
    onProgress?: (update: ProgressUpdate) => void
    isCancelled?: () => boolean
    checkpointEveryN?: number
    checkpointEveryMs?: number
}

const buildStatusMessage = (step: RunStep, done: number, total: number) => {
    const label = step === 'trial' ? 'Trial' : step === 'push' ? 'Push' : 'Email'
    return `${label} progress: ${done} / ${total}`
}

const sendPushOne = async (recipient: RecipientPreview, title: string, body: string, route: string): Promise<SendResult> => {
    if (!hasRealPushToken(recipient.deviceToken)) return 'skip'
    try {
        await customSendNotifyBody({
           // id: recipient.id,
            device: recipient.deviceToken!,
            title,
            text: body,
            url: route,
            silent: false,
        })
        return 'ok'
    } catch {
        return 'error'
    }
}

const buildDynamicForRecipient = (recipient: RecipientPreview, run: RunRecord) => {
    const base = run.payload.email.dynamicTemplateData ?? {}
   const fields = run.payload.email?.fields ?? ({} as any)
    const merged: any = {
        ...base,
        firstName: base.firstName ?? getFirstName(recipient.fullName),
        fullName: base.fullName ?? recipient.fullName,
        deepLink: base.deepLink ?? fields.deepLink,
        ctaLabel: base.ctaLabel ?? fields.ctaLabel,
    }

    if (run.payload.email.kind === 'trial') {
        merged.title = merged.title ?? fields.title
        merged.description = merged.description ?? fields.body
        merged.trialDays = merged.trialDays ?? fields.trialDays
    }

    if (run.payload.email.kind === 'unit_update') {
        merged.unitAddress = merged.unitAddress ?? fields.unitAddress
        merged.adminMessage = merged.adminMessage ?? fields.adminMessage
    }

    if (run.payload.email.kind === 'drop_price') {
        merged.unitAddress = merged.unitAddress ?? fields.unitAddress
        merged.unitPrice = merged.unitPrice ?? fields.unitPrice
        merged.unitDiscountPrice = merged.unitDiscountPrice ?? fields.unitDiscountPrice
        merged.adminMessage = merged.adminMessage ?? fields.adminMessage
    }

    if (run.payload.email.kind === 'account_blocked') {
        merged.adminMessage = merged.adminMessage ?? fields.adminMessage
        merged.termsUrl = merged.termsUrl ?? fields.termsUrl
        merged.supportEmail = merged.supportEmail ?? fields.supportEmail
        merged.supportPhone = merged.supportPhone ?? fields.supportPhone
    }

    if (run.payload.email.kind === 'account_unblocked') {
        merged.supportEmail = merged.supportEmail ?? fields.supportEmail
        merged.supportPhone = merged.supportPhone ?? fields.supportPhone
    }

    if (run.payload.email.kind === 'universal') {
        merged.title = merged.title ?? fields.title
        merged.description = merged.description ?? fields.body
    }

    return merged
}

const pickTemplateIdForRecipient = (recipient: RecipientPreview, run: RunRecord): string => {
    const kind = run.payload.email.kind
    const configuredTemplateId = run.payload.email.templateId

    // For main release events, route template by membership in standardEmails.
    if (kind === 'basicReleaseV2' || kind === 'basicReleaseForStandardUsersV2') {
        const recipientEmail = normalizeEmail(recipient.email)
        const isStandardUser = recipientEmail.length > 0 && STANDARD_EMAILS_SET.has(recipientEmail)
        return isStandardUser ? EMAIL_TEMPLATE_IDS.basicReleaseForStandardUsersV2 : EMAIL_TEMPLATE_IDS.basicReleaseV2
    }

    return configuredTemplateId
}

export const runSafeEvents = async (options: RunnerOptions): Promise<RunRecord> => {
    const checkpointEveryN = options.checkpointEveryN ?? DEFAULT_CHECKPOINT_EVERY_N
  if (!acquireRunLock(options.run.runId)) {
    return {
      ...options.run,
      status: 'failed',
      lastError: 'Another run is already locked in this browser. Cancel/finish it first.',
      statusMessage: 'Another run is already locked in this browser. Cancel/finish it first.',
      completedAt: new Date().toISOString(),
    }
  }

  setActiveRunIdLocal(options.run.runId)
  snapshotRunLocal(options.run)
  touchRunLock(options.run.runId)
    const defaultCounters: RunRecord['counters'] = {
        trialOk: 0,
        trialError: 0,
        pushOk: 0,
        pushSkip: 0,
        pushError: 0,
        emailOk: 0,
        emailSkip: 0,
        emailError: 0,
    }
    const counters: RunRecord['counters'] = {
        ...defaultCounters,
        ...(options.run.counters ?? {}),
    }
    let run: RunRecord = {
        ...options.run,
        status: 'running',
        counters,
    }
    const totalRecipients = options.recipients.length

    let recipientsSinceCheckpoint = 0

    const emitProgress = (step: RunStep) => {
        const done = Math.min(run.cursor.index, totalRecipients)
        const statusMessage = buildStatusMessage(step, done, totalRecipients)
        run.statusMessage = statusMessage
        options.onProgress?.({ step, done, total: totalRecipients, statusMessage, run })
    
      snapshotRunLocal(run)
        touchRunLock(run.runId)
        setActiveRunIdLocal(run.runId)

    }

    const checkpoint = async () => {
        await options.updateRun({ ...run })

               snapshotRunLocal(run)
        touchRunLock(run.runId)
        setActiveRunIdLocal(run.runId)

        recipientsSinceCheckpoint = 0
    }

    const checkpointIfNeeded = async () => {
        if (recipientsSinceCheckpoint >= checkpointEveryN) {
            await checkpoint()
        }
    }

    const failRun = async (message: string) => {
        run.status = 'failed'
        run.lastError = message
        run.statusMessage = message
        run.completedAt = new Date().toISOString()
        await checkpoint()
            releaseRunLock(run.runId)
    }

    const cancelRun = async () => {
        run.status = 'cancelled'
        run.statusMessage = 'Run cancelled.'
        run.completedAt = new Date().toISOString()
        await checkpoint()
        clearRunLocal(run.runId)
        clearActiveRunIdLocal()
        releaseRunLock(run.runId)
    }

    const shouldCancel = () => options.isCancelled?.() === true

    const shouldRunTrial = run.payload.email.enabled && run.payload.email.kind === 'trial'
    const shouldRunPush = run.payload.push.enabled !== false
    const shouldRunEmail = run.payload.email.enabled
    const stepsPerRecipient: RunStep[] = ['push', 'trial', 'email']
    const isStepEnabled = (step: RunStep): boolean => {
        if (step === 'push') return shouldRunPush
        if (step === 'trial') return shouldRunTrial
        return shouldRunEmail
    }
    const enabledSteps = stepsPerRecipient.filter(isStepEnabled)

    try {
        if (enabledSteps.length === 0) {
            run.cursor = { step: 'email', index: totalRecipients }
            run.status = 'completed'
            run.completedAt = new Date().toISOString()
            run.statusMessage = 'Run completed.'
            await checkpoint()
            clearRunLocal(run.runId)
            clearActiveRunIdLocal()
            releaseRunLock(run.runId)
            return run
        }

        let recipientIndex = Math.max(0, Math.min(run.cursor.index, totalRecipients))

        while (recipientIndex < totalRecipients) {
            if (shouldCancel()) {
                await cancelRun()
                return run
            }

            const recipient = options.recipients[recipientIndex]
            let startStepIndex = enabledSteps.indexOf(run.cursor.step)
            if (run.cursor.index !== recipientIndex || startStepIndex < 0) {
                startStepIndex = 0
            }

            for (let s = startStepIndex; s < enabledSteps.length; s++) {
                if (shouldCancel()) {
                    await cancelRun()
                    return run
                }

                const step = enabledSteps[s]
                run.cursor = { step, index: recipientIndex }

                if (step === 'trial') {
                    const ok = await grantRewardTrialForUser(recipient.id, run.payload.email.fields.trialDays)
                    if (ok) {
                        run.counters.trialOk += 1
                    } else {
                        run.counters.trialError += 1
                        await failRun(`Trial grant failed at index ${recipientIndex}.`)
                        return run
                    }
                }

                if (step === 'push') {
                    const dynPush = buildPushDynamicForRecipient(recipient, run)
                    const pushTitle = interpolate(run.payload.push.title, dynPush)
                    const pushBody = interpolate(run.payload.push.description, dynPush)
                    const pushRoute = interpolate(run.payload.push.deepLink, dynPush)
                    const result = await sendPushOne(recipient, pushTitle, pushBody, pushRoute)
                    if (result === 'ok') {
                        run.counters.pushOk += 1
                    } else if (result === 'skip') {
                        run.counters.pushSkip += 1
                    } else {
                        run.counters.pushError += 1
                        await failRun(`Push send failed at index ${recipientIndex}.`)
                        return run
                    }
                }

                if (step === 'email') {
                    const emailFilter = run.payload.email.recipientFilter
                    const isDropPriceFilteredOut = emailFilter?.mode === 'drop_price_active_ad' &&
                        !emailFilter.recipientIds.includes(recipient.id)
                    if (isDropPriceFilteredOut) {
                        // drop_price email is sent only to active advertisers on selected unit.
                        // Skip for others is expected behavior, not an error.
                        run.counters.emailSkip += 1
                    } else if (!recipient.email) {
                        run.counters.emailSkip += 1
                    } else {
                        const dynamicForRecipient = buildDynamicForRecipient(recipient, run)
                        const templateIdForRecipient = pickTemplateIdForRecipient(recipient, run)
                        const response = await sendMail({
                            email: recipient.email,
                            templateId: templateIdForRecipient,
                            dynamicTemplateData: dynamicForRecipient,
                        })
                        if (response.ok) {
                            run.counters.emailOk += 1
                        } else {
                            run.counters.emailError += 1
                            await failRun(`Email send failed at index ${recipientIndex}.`)
                            return run
                        }
                    }
                }

                const isLastStepForRecipient = s === enabledSteps.length - 1
                run.cursor = isLastStepForRecipient
                    ? { step: enabledSteps[0] ?? 'email', index: recipientIndex + 1 }
                    : { step: enabledSteps[s + 1], index: recipientIndex }

                if (step === 'push' && isLastStepForRecipient && recipientIndex < totalRecipients - 1) {
                    await sleep(1000)
                }

                if (isLastStepForRecipient) {
                    recipientsSinceCheckpoint += 1
                }

                emitProgress(step)
            }

            recipientIndex = run.cursor.index
            await checkpointIfNeeded()
        }

        run.cursor = { step: 'email', index: totalRecipients }
        run.status = 'completed'
        run.completedAt = new Date().toISOString()
        run.statusMessage = run.counters.emailSkip > 0
            ? `Run completed. Email skipped for ${run.counters.emailSkip} recipient(s).`
            : 'Run completed.'
        await checkpoint()
              clearRunLocal(run.runId)
        clearActiveRunIdLocal()
        releaseRunLock(run.runId)
        return run
    } catch (e: any) {
        const message = e?.message ?? 'Run failed.'
        await failRun(message)
        return run
    }
}

const buildPushDynamicForRecipient = (recipient: RecipientPreview, run: RunRecord) => {
  const fields = run.payload.email?.fields ?? ({} as any)

  return {
    firstName: getFirstName(recipient.fullName),
    fullName: recipient.fullName,

    unitAddress: fields.unitAddress,
    adminMessage: fields.adminMessage,
    trialDays: fields.trialDays,

    deepLink: run.payload.push.deepLink,
  }
}
